import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight, Plus, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import AddCicloButton from "./AddCicloButton";

/**
 * "Momentum meta della settimana" — Home hero card.
 *
 * Rules:
 *  - GET /api/momentum/weekly returns:
 *      { azione, next_cycle, needs_new_cycle }
 *  - If azione == null → render nothing (parent decides whether to show placeholder).
 *  - If needs_new_cycle → primary CTA becomes "+ Aggiungi ciclo per farla avanzare"
 *    using AddCicloButton with prefillMetaId=azione.id.
 *  - Otherwise, primary CTA is "Fai ora →" linking to /cicli/aperti focused on the next cycle.
 */
export default function MomentumCard({ onSaved }) {
  const [state, setState] = useState({ loading: true, data: null });

  const load = async () => {
    try {
      const d = await api.getMomentumWeekly();
      setState({ loading: false, data: d });
    } catch {
      setState({ loading: false, data: null });
    }
  };
  useEffect(() => { load(); }, []);

  if (state.loading) {
    return (
      <section className="pxos-card p-4" data-testid="momentum-card-loading">
        <div className="flex items-center gap-2 text-sm opacity-70">
          <Loader2 size={14} className="animate-spin"/> Momentum…
        </div>
      </section>
    );
  }
  const d = state.data;
  if (!d || !d.azione) return null;

  const a = d.azione;
  const n = d.next_cycle;
  const pct = Math.round(a.cached_progress || 0);

  const priorityBadge = {
    critical: { bg: "#ef4444", text: "#fff", label: "Critica" },
    high: { bg: "#f59e0b", text: "#fff", label: "Alta" },
    medium: { bg: "#95BAEF", text: "#0d1b2a", label: "Media" },
    low: { bg: "#e5e7eb", text: "#374151", label: "Bassa" },
  }[a.priority] || { bg: "#e5e7eb", text: "#374151", label: "" };

  const nextDate = n?.scheduled_date
    ? new Date(n.scheduled_date + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short" })
    : n?.due_date
      ? new Date(n.due_date + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short" })
      : null;

  return (
    <section
      className="pxos-card p-5 relative overflow-hidden"
      data-testid="momentum-card"
      style={{
        background: "linear-gradient(135deg, rgba(149,186,239,0.10), rgba(255,159,64,0.08))",
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame size={16} className="text-orange-500"/>
          <div className="text-[10px] uppercase tracking-widest opacity-70 font-mono">
            Momentum · Azione della settimana
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium"
          style={{ background: priorityBadge.bg, color: priorityBadge.text }}
          data-testid="momentum-card-priority"
        >
          {priorityBadge.label}
        </span>
      </div>

      <Link
        to={`/mete/${a.id}`}
        className="block mt-2 font-display text-xl sm:text-2xl font-semibold hover:underline"
        style={{ color: "var(--pxos-text-dark)" }}
        data-testid="momentum-card-title"
      >
        {a.title}
      </Link>

      {/* Progress bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs">
          <span className="opacity-70">Progresso</span>
          <span className="font-mono font-semibold" data-testid="momentum-card-progress">{pct}%</span>
        </div>
        <div className="h-2 mt-1 rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.06)" }}>
          <div
            className="h-full transition-all"
            style={{ width: `${pct}%`, background: "linear-gradient(90deg, #95BAEF, #f59e0b)" }}
          />
        </div>
      </div>

      {/* CTA */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {d.needs_new_cycle ? (
          <AddCicloButton
            variant="compact"
            label="Aggiungi ciclo per farla avanzare"
            prefillMetaId={a.id}
            onSaved={() => { load(); onSaved?.(); }}
            testid="momentum-card-add-ciclo"
          />
        ) : (
          <Link
            to="/cicli/aperti"
            className="pxos-btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
            data-testid="momentum-card-do-now"
          >
            Fai ora <ArrowRight size={12}/>
          </Link>
        )}
        {n && (
          <div className="text-xs opacity-80" data-testid="momentum-card-next-cycle">
            Prossimo: <span className="font-medium">{n.title}</span>
            {nextDate && <span className="opacity-70"> · {nextDate}{n.scheduled_time ? ` · ${n.scheduled_time}` : ""}</span>}
          </div>
        )}
        {d.needs_new_cycle && (
          <div className="text-xs opacity-70" data-testid="momentum-card-hint">
            Tutti i cicli collegati sono completati ma la meta non è ancora al 100%.
          </div>
        )}
      </div>
    </section>
  );
}
